<template>
  <v-container fluid class="pa-0">
    <v-row>
      <!-- Left sidebar -->
      <v-col cols="12" sm="12" :md="showFilters ? 4 : 2" class="pt-0" :class="{'mt-md-n10': !noMarginTop}">
        <div class="d-flex align-start">
          <div class="flex-grow-1">
            <MediaModeForm v-show="showFilters" v-if="(currentUser.clientType === 'Media' || currentUser.clientType === 'Driblab')" :fields="mediaModeFields"
              :mediaMode="mediaMode" @mediaModeChange="onMediaModeChange($event)"
              :mediaModeData="mediaModeData" @mediaModeDataChange="onMediaModeDataChange($event)" selectorMode></MediaModeForm>
            <v-card class="pb-2" elevation="0" outlined v-show="showFilters">
              <v-row class="mx-0 pt-0">
                <v-col cols="12" class="py-3">
                  <v-card-title class="pa-0 header-font pt-2" style="font-size: 22px;">{{ $t('general.filters') }}</v-card-title>
                </v-col>
                <!-- Display Type -->
                <v-col class="py-0" cols="12">
                  <p class="my-0 pa-0 primary--text font-weight-bold body-1">{{ $t('general.displayType') }}</p>
                  <v-radio-group v-model="displayModel" mandatory row hide-details class="pt-2 ma-0">
                    <template v-if="mode === 'pass-map'">
                      <v-radio value="map" color="secondary">
                        <template v-slot:label>
                          <span class="body-1 primary--text">{{ $t('game.passMap.pitchView') }} <v-icon small class="ms-1">border_horizontal</v-icon></span>
                        </template>
                      </v-radio>
                      <v-radio value="table" color="secondary" class="mr-0">
                        <template v-slot:label>
                          <span class="body-1 primary--text">{{ $t('charts.table') }} <v-icon small class="ms-1">table_rows</v-icon></span>
                        </template>
                      </v-radio>
                    </template>
                    <template v-if="mode === 'connections'">
                      <v-radio value="map" color="secondary">
                        <template v-slot:label>
                          <span class="body-1 primary--text">{{ $t('game.passMap.pitchView') }} <v-icon small class="ms-1">border_horizontal</v-icon></span>
                        </template>
                      </v-radio>
                      <v-radio value="matrix" color="secondary">
                        <template v-slot:label>
                          <span class="body-1 primary--text">{{ $t('game.passMap.passingMatrix') }} <v-icon small class="ms-1">grid_on</v-icon></span>
                        </template>
                      </v-radio>
                      <v-radio value="table" color="secondary" class="mr-0">
                        <template v-slot:label>
                          <span class="body-1 primary--text">{{ $t('charts.table') }} <v-icon small class="ms-1">table_rows</v-icon></span>
                        </template>
                      </v-radio>
                    </template>
                  </v-radio-group>
                </v-col>

                <!-- Orientation: Horizontal/Vertical -->
                <v-col cols="12" class="py-1 mt-2" v-if="displayModel === 'map'">
                  <p class="my-0 pa-0 primary--text font-weight-bold body-1 mb-1">{{ $t('general.orientation') }}</p>
                  <v-radio-group class="mt-0" v-model="orientationModel" row dense hide-details
                    @change="passMapKey += 1; passEventsKey += 1">
                    <v-radio color="secondary" value="horizontal">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('general.horizontal') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="vertical">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('general.vertical') }}</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-col>

                <!-- Team -->
                <v-col cols="12" class="py-1 my-2">
                  <p class="my-0 pa-0 primary--text font-weight-bold body-1 mb-1">{{ $t('game.passMap.team') }}:</p>
                  <v-radio-group class="mt-0 pt-0"
                    v-model="teamModel"
                    :column="$vuetify.breakpoint.xsOnly"
                    :row="!$vuetify.breakpoint.xsOnly"
                    hide-details
                    dense
                    @change="filterPasses('team')"
                    v-if="gameData"
                  >
                    <v-radio color="secondary" :label="gameData[0].teamName" :value="true">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ gameData[0].teamName }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" :label="gameData[1].teamName" :value="false">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ gameData[1].teamName }}</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                  <v-skeleton-loader v-else type="text" class="mt-6 ml-lg-4"></v-skeleton-loader>
                </v-col>

                <!-- Minutes (hidden for Pass Map → Table: table uses full-game passing stats) -->
                <v-col cols="12" class="pb-2 pt-0 my-0" v-if="mode !== 'pass-map' || displayModel !== 'table'">
                  <v-range-slider
                    :label="$t('game.passMap.minutes')"
                    thumb-label
                    v-model="minutes"
                    :min="rangeMinutes[0]"
                    :max="rangeMinutes[1]"
                    step="1"
                    hide-details
                    :color="`secondary ${$vuetify.theme.dark ? 'lighten-3' : ''}`"
                    class="align-center"
                    @change="filterPasses('minutes'); passEventsKey += 1"
                  >
                    <template v-slot:label>
                      <div class="d-flex flex-column">
                        <span class="primary--text font-weight-bold body-1">{{ $t('game.passMap.minutes') }}</span>
                        <span class="body-2 subheader-font monospace-font">{{ `(${rangeMinutes[0]} - ${rangeMinutes[1]})` }}</span>
                      </div>
                    </template>
                  </v-range-slider>
                </v-col>

                <!-- Passer filter (pass-map tab, map display) -->
                <v-col cols="12" class="py-1 mt-2" v-if="mode === 'pass-map' && displayModel === 'map'">
                  <p class="my-0 pa-0 primary--text font-weight-bold body-1">{{ $t('game.passMap.passer') }}</p>
                  <v-autocomplete
                    v-model="selectedPlayer"
                    :items="[{text: $t('game.passMap.all'), value: ''}, ...teamPlayerNames.map(p => ({text: p, value: p}))]"
                    item-text="text"
                    item-value="value"
                    class="body-1 mb-2"
                    dense
                    hide-details
                    clearable
                    @change="passEventsKey++"
                  >
                    <template v-slot:item="{item}">
                      <span class="body-2 primary--text">{{ item.text }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Receiver filter (pass-map tab, map display) — requires backend to add rN field to getEvents response -->
                <v-col cols="12" class="py-1 mt-2" v-if="mode === 'pass-map' && displayModel === 'map' && hasReceiverData">
                  <p class="my-0 pa-0 primary--text font-weight-bold body-1">{{ $t('game.passMap.receiver') || 'Receiver' }}</p>
                  <v-autocomplete
                    v-model="selectedReceiver"
                    :items="[{text: $t('game.passMap.all'), value: ''}, ...teamPlayerNames.map(p => ({text: p, value: p}))]"
                    item-text="text"
                    item-value="value"
                    class="body-1 mb-2"
                    dense
                    hide-details
                    clearable
                    @change="passEventsKey += 1"
                  >
                    <template v-slot:item="{item}">
                      <span class="body-2 primary--text">{{ item.text }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!--Visualization-->
                <v-col class="pb-1" cols="12" sm="12">
                  <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2 mb-1">{{ $t('arrigo.visualization') }}</p>
                  <div class="d-flex mt-0 pa-0 ma-0">
                    <v-checkbox
                      v-model="showCircles"
                      color="secondary"
                      dense hide-details
                      class="mr-4 pa-0 ma-0"
                      data-testid="lbp-team-circles-checkbox"
                      @change="passEventsKey++"
                    >
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('finishingMap.circles') }}</span>
                      </template>
                    </v-checkbox>
                    <v-checkbox
                      v-model="showLines"
                      color="secondary"
                      dense hide-details
                      class="mr-4 pa-0 ma-0"
                      data-testid="lbp-team-lines-checkbox"
                      @change="passEventsKey++"
                    >
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('finishingMap.lines') }}</span>
                      </template>
                    </v-checkbox>
                  </div>
                </v-col>

                <!-- Pass Outcome (pass-map tab, map display) -->
                <v-col cols="12" class="py-1 mt-2" v-if="mode === 'pass-map' && displayModel === 'map'">
                  <v-radio-group class="mt-0"
                    v-model="passOutcome"
                    column
                    dense
                    hide-details
                    @change="passEventsKey += 1"
                  >
                    <template v-slot:label>
                      <div class="mr-8 primary--text font-weight-bold body-1">{{ $t('game.passMap.passOutcome') || 'Pass Outcome' }}</div>
                    </template>
                    <v-radio color="secondary" value="all">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.all') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="successful">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('eventChart.successful') || 'Successful' }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="unsuccessful">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('eventChart.unsuccessful') || 'Unsuccessful' }}</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-col>

                <!-- Pass Direction (pass-map tab, map display) -->
                <v-col cols="12" class="py-1 mt-2" v-if="mode === 'pass-map' && displayModel === 'map'">
                  <v-radio-group class="mt-0"
                    v-model="passDirection"
                    column
                    dense
                    hide-details
                    @change="passEventsKey += 1"
                  >
                    <template v-slot:label>
                      <div class="mr-8 primary--text font-weight-bold body-1">{{ $t('game.passMap.passDirection') || 'Pass Direction' }}</div>
                    </template>
                    <v-radio color="secondary" value="all">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.all') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="forward">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.forwards') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="backward">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.backwards') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="sideways">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.sideways') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="longBall">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.longBall') }}</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-col>

                <!-- Half filter (pass-map tab, map display) -->
                <v-col cols="12" class="py-1 mt-2" v-if="mode === 'pass-map' && displayModel === 'map'">
                  <v-radio-group class="mt-0"
                    v-model="passHalf"
                    column
                    dense
                    hide-details
                    @change="passEventsKey += 1"
                  >
                    <template v-slot:label>
                      <div class="mr-8 primary--text font-weight-bold body-1">{{ $t('game.passMap.half') || 'Half' }}</div>
                    </template>
                    <v-radio color="secondary" value="all">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.all') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="offensive">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.offensive') || 'Offensive' }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" value="defensive">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.defensive') || 'Defensive' }}</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-col>

                <!-- Pass Type (connections tab only) -->
                <v-col cols="12" class="py-1 mt-2" v-if="mode === 'connections'">
                  <v-radio-group class="mt-0"
                    v-model="passesType"
                    column
                    dense
                    hide-details
                    @change="filterPasses()"
                  >
                    <template v-slot:label>
                      <div
                        class="mr-8 primary--text font-weight-bold body-1"
                      >{{$t('game.passMap.select')}}:</div>
                    </template>
                    <v-radio color="secondary" :label="$t('game.passMap.all')" value="all">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.all') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" :label="$t('game.passMap.attacking')" value="isAttacking">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.attacking') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" :label="$t('game.passMap.defending')" value="isDefending">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.defending') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" :label="$t('game.passMap.forwards')" value="isForward">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.forwards') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" :label="$t('game.passMap.backwards')" value="isBackward">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.backwards') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" :label="$t('game.passMap.sideways')" value="isSideways">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.sideways') }}</span>
                      </template>
                    </v-radio>
                    <v-radio color="secondary" :label="$t('game.passMap.longBall')" value="isLongBall">
                      <template v-slot:label>
                        <span class="body-1 primary--text">{{ $t('game.passMap.longBall') }}</span>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-divider class="mt-4 mr-2"></v-divider>
              <v-row>
                <v-col class="d-flex justify-end pt-2 pb-0 px-4" cols="12">
                  <v-btn
                    color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                    :x-small="!$vuetify.breakpoint.xsOnly"
                    :disabled="!hasChartToExport"
                    v-on:click.native="saveAsPNG()"
                  >
                    <v-icon small class="mr-1">file_download</v-icon>
                    <span class>PNG</span>
                  </v-btn>
                  <v-btn color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                    :x-small="!$vuetify.breakpoint.xsOnly"
                    :disabled="!hasChartToExport"
                    v-on:click.native="saveAsSVG()">
                    <v-icon small>file_download</v-icon>
                    <span>SVG</span>
                  </v-btn>
                  <v-btn
                    color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                    :x-small="!$vuetify.breakpoint.xsOnly"
                    :loading="loadingExport"
                    :disabled="!hasExportableElement || !hasChartToExport"
                    v-on:click.native="onGenerateIFrameClick()"
                  >
                    <v-icon small class="mr-1"
                      >share</v-icon
                    >
                    <span>Embed</span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </div>
          <div class="flex-shrink-0 ml-2 align-self-start">
            <slot name="filters-toggle"></slot>
          </div>
        </div>
      </v-col>

      <!-- Right content -->
      <v-col cols="12" sm="12" :md="showFilters ? 8 : 10" id="chart" ref="chart" :class="['pt-0', chartColumnAlignClass]">
        <template v-if="mode === 'pass-map'">
          <!-- Map display (individual passes on pitch) -->
          <div v-if="displayModel === 'map'">
            <template v-if="size > 0 && gameData && passEventsLoaded">
              <GameAvatarMedia v-if="avatarSize && mediaMode && game"
                :key="`avatar-${orientationModel}`" 
                :width="avatarSize.width" :height="avatarSize.height" :id="`${gameId}-${chartId}`" :game="game"
                :margin="margin" 
                :titleLeft="isMobile ? game.homeTeamAlias : game.homeTeamName" 
                :titleRight="isMobile ? game.awayTeamAlias : game.awayTeamName"
                :leftColor="isWhite(mediaModeData.leftFillColor) ? mediaModeData.leftBorderColor : mediaModeData.leftFillColor"
                :rightColor="isWhite(mediaModeData.rightFillColor) ? mediaModeData.rightBorderColor : mediaModeData.rightFillColor"
                ref="gameAvatarMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
                type="passMap" :lineDistance="mediaModeData.lineLength" :avatarSize="mediaModeData.avatarSize" :lineWeight="mediaModeData.lineWeight"
                :titleFontSize="mediaModeData.titleFontSize" :titleFontWeight="mediaModeData.titleFontWeight"
                :subtitle="mediaModeData.subtitle" :subtitleFontSize="mediaModeData.subtitleFontSize" :subtitleFontWeight="mediaModeData.subtitleFontWeight"
                :subtitle2="mediaModeData.subtitle2" :subtitle2FontSize="mediaModeData.subtitle2FontSize" :subtitle2FontWeight="mediaModeData.subtitle2FontWeight"
                :isVertical="orientationModel === 'vertical'">
              </GameAvatarMedia>

              <PassMapChartD3
                :key="passEventsKey"
                :id="chartId"
                :size="size"
                :passes="filteredPassEventsForChart"
                :passOutcome="passOutcome !== 'all' ? (passOutcome === 'successful' ? ($t('eventChart.successful') || 'Successful') : ($t('eventChart.unsuccessful') || 'Unsuccessful')) : ''"
                :passDirection="passDirection !== 'all' ? $t(`game.passMap.${passDirection === 'forward' ? 'forwards' : passDirection === 'backward' ? 'backwards' : passDirection}`) : ''"
                :half="passMapFilterLine"
                :showCircles="showCircles"
                :showLines="showLines"
                :mediaMode="mediaMode"
                :legendTitle="legendTitle"
                :legendTeamCrest="legendTeamCrest"
                :legendTeamName="legendTeamName"
                :legendSubtitle="legendSubtitle"
                :legendMetrics="legendMetrics"
                :orientation="orientationModel"
                @sizeChange="onChartSizeChange($event)"
                @loaded="onLoaded($event)"
                >
              </PassMapChartD3>

              <DriblabSocialMedia v-if="game && avatarSize && size && mediaMode && mediaModeData.socialMedia"  :width="avatarSize.width" :height="50" :id="`${gameId}-${chartId}`"
                :margin="margin" ref="driblabSocialMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
                :fontSize="isMobile ? '12' : '14'" :fontWeight="mediaModeData.socialMediaFontWeight"
                :logoCfg="mediaModeData.logo" :sponsor="mediaModeData.sponsor.dataUrl" type="finishingMap" 
                :isVertical="orientationModel === 'vertical'"
                :isGame="true">
              </DriblabSocialMedia>

              <!-- Legacy PDF: same DOM ids as before (pitchPassMap / pitchPassMatrix). Hidden; not used for on-screen UI. -->
              <div v-show="showMapsForPDF" aria-hidden="true" class="report-capture-legacy-pass-maps">
                <PassMapChart
                  v-if="size != 0 && filteredPasses.length > 0 && game"
                  :id="`${gameId}-${teamId}-${chartId}`"
                  :size="size"
                  :margin="margin"
                  :key="`report-passmap-${passMapKey}`"
                  :passes="filteredPasses"
                  :passesByPlayersName="passesByPlayersName"
                  :playersAvgPosition="playersAvgPosition"
                  :teamId="teamId"
                  :teamFormation="teamFormation"
                  :teamColor="teamModel ? mediaModeData.leftFillColor : mediaModeData.rightFillColor"
                  :strokeColor="strokeColor"
                  :teamName="teamModel ? mediaModeData.titleLeft : mediaModeData.titleRight"
                  :minFrom="minutes[0]"
                  :minTo="minutes[1]"
                  :passesType="passesType"
                  :language="currentUserLanguage"
                  :mediaMode="false"
                  :orientation="orientationModel"
                  @loaded="onLoaded($event)"
                />
                <PassMatrix
                  v-if="size != 0 && filteredPassesMatrix.length > 0 && game"
                  :id="`${gameId}-${teamId}-${chartId}`"
                  :size="size"
                  :margin="margin"
                  :key="`report-matrix-${passMatrixKey}`"
                  :passes="filteredPassesMatrix"
                  :homeTeamScore="game.homeTeamScore"
                  :rivalTeamScore="game.awayTeamScore"
                  :teamName="homeTeamName"
                  :rivalTeamName="awayTeamName"
                  :minFrom="minutes[0]"
                  :minTo="minutes[1]"
                  :themeDark="false"
                  :language="currentUserLanguage"
                  @loaded="onLoaded($event)"
                />
              </div>
            </template>
            <div v-else-if="!gameData || !passEventsLoaded" class="d-flex justify-center align-center" style="min-height: 300px;">
              <v-progress-circular indeterminate color="secondary"></v-progress-circular>
            </div>
          </div>
          <!-- Table display (Passing Stats) -->
          <div v-if="displayModel === 'table'" id="passMapTableExport" class="pass-map-table-export-wrap">
            <div class="rounded text-center pa-2 pt-3" :class="$vuetify.theme.dark ? 'secondary' : 'primary'">
              <span class="white--text header-font" style="font-size: 18px;">{{$t('table.playerPassMapData.title')}}</span>
            </div>
            <v-data-table
              id="passMapTable"
              :headers="passingStatsHeader"
              :items="passingStatsItems"
              :items-per-page="$vuetify.breakpoint.xlOnly ? itemsPerPageData : 10"
              class="elevation-1 mt-3"
              dense
            >
              <template
                v-for="(h) in passingStatsHeader"
                v-slot:[`header.${h.value}`]="{ header }"
              >
                <v-tooltip top color="lightSecondary lighten-4" max-width="300" :key="header.value">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{h.text}}</span>
                  </template>
                  <span class="black--text body-1">{{h.tooltip}}</span>
                </v-tooltip>
              </template>
              <template v-slot:item.playerName="{ item }">
                <a :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'" :href="'/player/' + item.playerId">{{ item.playerName }}</a>
              </template>
              <template v-slot:item.pctPasses="{ item }">
                <span>{{ formatDecimal(item.pctPasses, 0) }}%</span>
              </template>
              <template v-slot:item.xGChain="{ item }">
                <div class="w-100 h-100 d-flex align-center justify-center font-weight-bold" v-bind:style="{'background-color':getBackgroundColor(item,'xGChain')}"
                 v-bind:class="getTextColorClass(item,'xGChain')">
                  <span>{{ formatDecimal(item.xGChain, 2) }}%</span>
                </div>
              </template>
              <template v-slot:item.xA="{ item }">
                <div class="w-100 h-100 d-flex align-center justify-center font-weight-bold" v-bind:style="{'background-color':getBackgroundColor(item,'xA')}"
                 v-bind:class="getTextColorClass(item,'xA')">
                  <span>{{ formatDecimal(item.xA, 2) }}</span>
                </div>
              </template>
              <template v-slot:item.xT="{ item }">
                <div class="w-100 h-100 d-flex align-center justify-center font-weight-bold" v-bind:style="{'background-color':getBackgroundColor(item,'xT')}"
                 v-bind:class="getTextColorClass(item,'xT')">
                  <span>{{ formatDecimal(item.xT, 2) }}%</span>
                </div>
              </template>
            </v-data-table>
          </div>
        </template>

        <!-- Connections sub-tab -->
        <template v-if="mode === 'connections'">
          <!-- Matrix display -->
          <div v-if="displayModel === 'matrix'" id="passMatrixExport" class="pass-matrix-export-wrap">
            <PassMatrix
              v-if="size != 0 && filteredPassesMatrix.length > 0 && game"
              :id="`${gameId}-${teamId}-${chartId}`"
              :size="size"
              :margin="margin"
              :key="passMatrixKey"
              :passes="filteredPassesMatrix"
              :homeTeamScore="game.homeTeamScore"
              :rivalTeamScore="game.awayTeamScore"
              :teamName="homeTeamName"
              :rivalTeamName="awayTeamName"
              :minFrom="minutes[0]"
              :minTo="minutes[1]"
              :themeDark="$vuetify.theme.dark"
              :language="currentUserLanguage"
              @loaded="onLoaded($event)"
            />
          </div>
          <!-- Map display (avg positions + passing web) -->
          <div v-if="displayModel === 'map'">
            <GameAvatarMedia v-if="avatarSize && mediaMode && game"
              :key="`avatar-${orientationModel}`"
              :width="avatarSize.width" :height="avatarSize.height" :id="`${gameId}-${chartId}`" :game="game"
              :margin="margin" 
              :titleLeft="isMobile ? game.homeTeamAlias : game.homeTeamName" 
              :titleRight="isMobile ? game.awayTeamAlias : game.awayTeamName"
              :leftColor="isWhite(mediaModeData.leftFillColor) ? mediaModeData.leftBorderColor : mediaModeData.leftFillColor"
              :rightColor="isWhite(mediaModeData.rightFillColor) ? mediaModeData.rightBorderColor : mediaModeData.rightFillColor"
              ref="gameAvatarMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
              type="passMap" :lineDistance="mediaModeData.lineLength" :avatarSize="mediaModeData.avatarSize" :lineWeight="mediaModeData.lineWeight"
              :titleFontSize="mediaModeData.titleFontSize" :titleFontWeight="mediaModeData.titleFontWeight"
              :subtitle="mediaModeData.subtitle" :subtitleFontSize="mediaModeData.subtitleFontSize" :subtitleFontWeight="mediaModeData.subtitleFontWeight"
              :subtitle2="mediaModeData.subtitle2" :subtitle2FontSize="mediaModeData.subtitle2FontSize" :subtitle2FontWeight="mediaModeData.subtitle2FontWeight"
              :isVertical="orientationModel === 'vertical'">
            </GameAvatarMedia>
            <PassMapChart
              v-if="size != 0 && filteredPasses.length > 0 && game"
              :id="`${gameId}-${teamId}-${chartId}`"
              :size="size"
              :margin="margin"
              :key="passMapKey"
              :passes="filteredPasses"
              :passesByPlayersName="passesByPlayersName"
              :playersAvgPosition="playersAvgPosition"
              :teamId="teamId"
              :teamFormation="teamFormation"
              :teamColor="teamModel ? mediaModeData.leftFillColor : mediaModeData.rightFillColor"
              :strokeColor="strokeColor"
              :teamName="teamModel ? mediaModeData.titleLeft : mediaModeData.titleRight"
              :minFrom="minutes[0]"
              :minTo="minutes[1]"
              :passesType="passesType"
              :language="currentUserLanguage"
              :mediaMode="mediaMode"
              :orientation="orientationModel"
              @sizeChange="onChartSizeChange($event)"
              @loaded="onLoaded($event)"
            />
            <DriblabSocialMedia v-if="game && avatarSize && size && mediaMode && mediaModeData.socialMedia"  :width="avatarSize.width" :height="50" :id="`${gameId}-${chartId}`"
              :margin="margin" ref="driblabSocialMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
              :fontSize="isMobile ? '12' : '14'" :fontWeight="mediaModeData.socialMediaFontWeight"
              :logoCfg="mediaModeData.logo" :sponsor="mediaModeData.sponsor.dataUrl" type="finishingMap" 
              :isVertical="orientationModel === 'vertical'"
              :isGame="true">
            </DriblabSocialMedia>
          </div>
          <!-- Table display (Connections) -->
          <div v-if="displayModel === 'table'" id="passMapConnectionsTableExport" class="pass-map-table-export-wrap">
            <div class="rounded text-center pa-2 pt-3" :class="$vuetify.theme.dark ? 'secondary' : 'primary'">
              <span class="white--text header-font" style="font-size: 18px;">{{$t('game.passMap.passingConnections')}}</span>
            </div>
            <v-data-table
              id="passMapConnectionsTable"
              :headers="connectionsHeader"
              :items="connectionsItems"
              :items-per-page="$vuetify.breakpoint.xlOnly ? itemsPerPageConnections : 10"
              class="elevation-1 mt-3"
              dense
            >
              <template v-slot:item.playerFrom="{ item }">
                <a :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'" :href="'/player/' + playerNamePlayerIdMap[item.playerFrom]">{{ item.playerFrom }}</a>
              </template>
              <template v-slot:item.playerTo="{ item }">
                <a :class="$vuetify.theme.dark ? 'primary--text' : 'secondary--text text--darken-2'" :href="'/player/' + playerNamePlayerIdMap[item.playerTo]">{{ item.playerTo }}</a>
              </template>
            </v-data-table>
          </div>
        </template>
      </v-col>
    </v-row>
    <ChartExportLinksModal ref="chartExportLinksModalRef" :title="`${game.homeTeamName} ${game.homeTeamScore} - ${game.awayTeamScore} ${game.awayTeamName}`" v-if="game"
    :subtitle="$t('game.header.gamePassMap')"></ChartExportLinksModal>
  </v-container>
</template>

<script>
import * as d3 from "d3";

import GameService from "../../services/game/game.service";
import PassMapChart from "../base/PassMapChart";
import PassMatrix from "../base/PassMatrix";
import MediaModeForm from "../base/MediaModeForm";
import DriblabSocialMedia from "../base/DriblabSocialMedia";
import Dictionary from "../../dictionary";
import GameAvatarMedia from "./GameAvatarMedia";
import PassMapChartD3 from "../base/PassMapChartD3.vue";
import * as html2canvas from 'html2canvas';
import { exportSvg, downloadPng, copyAndAppendElement } from "../../utils/exportToPNG";
import { imageUrlToBase64 } from "../../utils/functions";
import { BLACK_LOGO_DATA_URI } from "../../assets/svg/driblabLogoBlackSVG";

/** html2canvas clones SVG into a context where bundled asset URLs often fail to load; data URIs work. */
let driblabWhiteLogoDataUriPromise;
function getDriblabWhiteLogoDataUri() {
  if (!driblabWhiteLogoDataUriPromise) {
    const url = require("@/assets/img/driblab_white.png");
    driblabWhiteLogoDataUriPromise = fetch(url)
      .then((r) => r.blob())
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
      );
  }
  return driblabWhiteLogoDataUriPromise;
}
import { getNeutralColors,userHasRole } from "../../utils/functions";
import ChartExportLinksModal from '../base/ChartExportLinksModal';
import { v4 } from 'uuid';
import UserChartService from '../../services/user-chart.service';
import DocumentService from '../../services/document.service';
import UserService from '../../services/user.service';

const NODE_ENV = process.env.NODE_ENV;
const PROTOCOL = process.env.PROTOCOL || 'http://';
const END_POINT_URL = process.env.END_POINT_URL || window.location.hostname;
const END_POINT_PORT = process.env.END_POINT_PORT || '8081';

const API_URL = NODE_ENV === 'production' ? 'https://pro.driblab.com/embed-chart/' : PROTOCOL + END_POINT_URL + ':' + END_POINT_PORT + '/embed-chart/'

export default {
  name: "GamePassMap",
  components: {
    PassMapChart,
    PassMatrix,
    MediaModeForm,
    GameAvatarMedia,
    DriblabSocialMedia,
    ChartExportLinksModal,
    PassMapChartD3
  },
  props:{
    chartId:{
      type:Number,
      default: 0
    },
    gameId:{
      type:Number,
      required: true
    },
    showFilters: {
      type: Boolean,
      default: true
    },
    noMarginTop: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'pass-map',
      validator: v => ['pass-map', 'connections'].includes(v)
    },
    homeTeam: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      required: false
    },
    showMapsForPDF: {
      type: Boolean,
      default: false
    },
  },
  watch: {
    "mediaMode": function(newVal, oldVal) {
      if(!this.mediaMode){
        this.mediaModeData = {leftBorderColor:"#3366FF",rightBorderColor:"#3366FF",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64"
                      ,subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}}
        this.mediaModeData.subtitle = this.game.leagueName;
        this.mediaModeData.subtitle2 = this.$t("game.header.gamePassMap");
        this.mediaModeData.titleLeft = this.game.homeTeamName;
        this.mediaModeData.titleRight = this.game.awayTeamName;
        this.mediaModeData.leftFillColor = this.game.homeTeamColor ? this.game.homeTeamColor.replace("0x","#") : getNeutralColors('home')[0];
        this.mediaModeData.rightFillColor = this.game.awayTeamColor ? this.game.awayTeamColor.replace("0x","#") : getNeutralColors('away')[0];
        this.mediaModeData.leftBorderColor = this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor.replace("0x","#") : getNeutralColors('home')[1];
        this.mediaModeData.rightBorderColor = this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor.replace("0x","#") : getNeutralColors('away')[1];
        this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
      }
      this.mediaModeData.bgColor = this.mediaMode ? '#ffffff' : '#ffffff0';
      this.onWindowResize(null,false)
    },
    showFilters() {
      this.$nextTick(() => {
        if (this.$refs.chart) {
          this.size = this.$refs.chart.clientWidth * 0.95;
          this.onWindowResize(null, true);
        }
      });
    },
    displayModel(newVal) {
      if (this.mode !== 'pass-map' || newVal !== 'table' || !this.gameDataByTeam || !this.gameDataByTeam.playersData) return;
      this.passingStatsItems = this.gameDataByTeam.playersData;
      this.$emit('passingStats', this.passingStatsItems);
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserLanguage() {
      return this.language ? this.language : this.$store.getters["auth/language"];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown || this.$vuetify.breakpoint.mobile;
    },
     avatarSize() {
      if (this.orientationModel === 'horizontal') {
        return {
          width: this.size,
          height: this.size / 6.77
        }
      }
      return {
        width: this.size / 1.49,
        height: this.size / 5
      }
    },
    lastSearchId() {
      return this.$store.state.auth.lastSearch;
    },
    darkMode(){
      return this.currentUser.darkMode || this.$vuetify.theme.dark;
    },
    teamPlayerNames() {
      if (!this.gameData) return [];
      const teamData = this.teamModel ? this.gameData[0] : this.gameData[1];
      const avgNames = teamData.playersAvgPosition.map(p => p.playerName);
      const dataNames = teamData.playersData ? teamData.playersData.map(p => p.playerName) : [];
      return [...new Set([...avgNames, ...dataNames])].sort();
    },
    hasReceiverData() {
      return this.passEvents.some(e => this.getReceiverName(e) != null);
    },
    filteredPassEventsForChart() {
      if (!this.passEvents.length || !this.gameData) return [];
      const teamName = this.teamModel ? this.gameData[0].teamName : this.gameData[1].teamName;
      const minFrom = this.minutes[0];
      const minTo = this.minutes[1];
      return this.passEvents
        .filter(e => {
          if (e.e !== 'Pass' && e.e !== 'OffsidePass') return false;
          if (e.min < minFrom || e.min > minTo) return false;
          if (this.selectedPlayer && e.pN !== this.selectedPlayer) return false;
          if (this.selectedReceiver && this.getReceiverName(e) !== this.selectedReceiver) return false;
          if (this.passOutcome !== 'all' && (this.passOutcome === 'successful' ? e.o !== true : e.o !== false)) return false;
          if (this.passDirection !== 'all' && this.getPassDirection(e) !== this.passDirection) return false;
          if (this.passHalf !== 'all' && (this.passHalf === 'offensive' ? e.x < 50 : e.x >= 50)) return false;
          return true;
        })
        .map(e => ({
          ...e,
          player: teamName,
          game: e.pN,
          isHomeTeam: true,
          width: 30,
          eventLabel: e.e
        }));
    },
    passMapFilterLine() {
      const parts = [];
      if (this.game) parts.push(`${this.game.homeTeamName} vs ${this.game.awayTeamName}`);
      if (this.minutes[0] !== this.rangeMinutes[0] || this.minutes[1] !== this.rangeMinutes[1]) {
        parts.push(`${this.minutes[0]}' - ${this.minutes[1]}'`);
      }
      if (this.passHalf !== 'all') {
        parts.push(this.passHalf === 'offensive'
          ? (this.$t('game.passMap.offensive') || 'Offensive Half')
          : (this.$t('game.passMap.defensive') || 'Defensive Half'));
      }
      return parts.join(' · ');
    },
    passMapTeamNameForLegend() {
      if (!this.gameData) return '';
      return this.teamModel ? this.gameData[0].teamName : this.gameData[1].teamName;
    },
    /** Right-side tally on EventsChart: explain filter match when raw events exist but none pass filters. */
    passMapPassEventsEmptyTallyText() {
      if (!this.passEvents.length || this.filteredPassEventsForChart.length > 0) return '';
      return this.$t('general.noDataGivenFilters') || '';
    },
    hasChartToExport() {
      return (this.mode === 'pass-map' && this.displayModel === 'map' && this.filteredPassEventsForChart.length > 0) ||
             (this.mode === 'pass-map' && this.displayModel === 'table' && this.passingStatsItems.length > 0) ||
             (this.mode === 'connections' && this.displayModel === 'matrix') ||
             (this.mode === 'connections' && this.displayModel === 'map') ||
             (this.mode === 'connections' && this.displayModel === 'table' && this.connectionsItems.length > 0);
    },
    currentChartType() {
      if (this.mode === 'connections' && this.displayModel === 'matrix') return 'PassMatrix';
      if (this.mode === 'connections' && this.displayModel === 'map') return 'PassMap';
      return 'PassMap';
    },
    hasExportableElement() {
      return (this.mode === 'pass-map' && this.displayModel === 'map' && this.size > 0) ||
             (this.mode === 'pass-map' && this.displayModel === 'table' && this.passingStatsItems && this.passingStatsItems.length > 0) ||
             (this.mode === 'connections' && (this.displayModel === 'matrix' || this.displayModel === 'map')) ||
             (this.mode === 'connections' && this.displayModel === 'table' && this.connectionsItems && this.connectionsItems.length > 0);
    },
    /** Pass matrix SVG is fixed-width; centering the column left a large empty gap beside the filters */
    chartColumnAlignClass() {
      return this.mode === 'connections' && this.displayModel === 'matrix' ? 'text-left' : 'd-flex justify-center';
    },
    legendTitle() {
      if (this._isDestroyed || !this.$t) {
        return '';
      }
      return this.$t("game.header.gamePassMap");
    },
    legendTeamCrest() {
      if (!this.teamId) {
        return 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png';
      }
      return `https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/${this.teamId}.png`;
    },
    legendTeamName() {
      return this.teamName;
    },
    legendSubtitle() {
      if (this._isDestroyed || !this.$t) {
        return '';
      }
      return `${this.game.leagueName} · ${this.game.homeTeamName} ${this.game.homeTeamScore} : ${this.game.awayTeamScore} ${this.game.awayTeamName}`;
    },
    legendMetrics() {
      if (!this.passEvents) {
        return { total: 0, successful: 0, rate: '0%' };
      }
      const data = (!this.filteredPassEventsForChart || this.filteredPassEventsForChart.length === 0) ? this.passEvents : this.filteredPassEventsForChart;
      const total = data.length;
      const successful = data.filter(p => p.o === true).length;
      const rate = total > 0 ? ((successful / total) * 100).toFixed(2) : 0;
      return {
        line1: `${total} ${this.$t("eventChart.totalPasses")}`,
        line2: `(${successful} ${this.$t("eventChart.successfulEvent")} · ${rate}% ${this.$t("arrigo.successRate")})`
      };
    }
  },
  data() {
    return {
      displayModel: 'map',
      passEvents: [],
      passEventsLoaded: false,
      passEventsKey: 2000,
      selectedPlayer: '',
      selectedReceiver: '',
      passOutcome: 'all',
      passDirection: 'all',
      passHalf: 'all',
      size: 0,
      margin: null,
      game:null,
      teamId: 0,
      //True = Home team ; False = Away team
      teamModel: this.homeTeam,
      homeTeamName: "",
      awayTeamName: "",
      teamName: "",
      teamFormation: "",
      teamColor: "",
      strokeColor: "#FFFFFF",
      passMapKey: 0,
      passMatrixKey: 1000,
      passesType: "all",
      orientationModel: "horizontal",
      passes: null,
      passesByPlayersName: null,
      gameData: null,
      gameDataByTeam: null,
      filteredPasses: [],
      playersAvgPosition: [],
      minutes: [0, 120],
      rangeMinutes: [0,120],
      periodId: 0,
      itemsPerPageData: 15,
      itemsPerPageConnections: 15,
      connectionsHeader: [
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["connections"]["playerFrom"],
          align: "center",
          value: "playerFrom",
          sortable: false
        },
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["connections"]["playerTo"],
          align: "center",
          value: "playerTo",
          sortable: false
        },
        {
          text: Dictionary[this.$i18n.locale]["table"]["connections"]["passes"],
          align: "center",
          value: "numberOfPasses",
          sortable: true
        }
      ],
      connectionsItems: [],
      passingStatsHeader: [
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "player"
            ],
          tooltip:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "playerTooltip"
            ],
          width: "30%",
          align: "center",
          value: "playerName",
          sortable: true
        },
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "passes"
            ],
          tooltip:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "passesTooltip"
            ],
          width: "15%",
          align: "center",
          value: "totalPasses",
          sortable: true
        },
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "passesPct"
            ],
          tooltip:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "passesPctTooltip"
            ],
          width: "20%",
          align: "center",
          value: "pctPasses",
          sortable: true
        },
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"]["xA"],
          tooltip:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "xATooltip"
            ],
          width: "8%",
          align: "center",
          value: "xA",
          sortable: true
        },
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "xG"
            ],
          tooltip:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "xGTooltip"
            ],
          width: "8%",
          align: "center",
          value: "xGChain",
          sortable: true
        },
        {
          text:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"]["xT"],
          tooltip:
            Dictionary[this.$i18n.locale]["table"]["playerPassMapData"][
              "xTTooltip"
            ],
          width: "19%",
          align: "center",
          value: "xT",
          sortable: true
        }
      ],
      passingStatsItems: [],
      playerNamePlayerIdMap: null,
      playerIdsWithMoreMinutesPlayed: [],
      mediaMode: false,
      mediaModeData: {leftBorderColor:"#3366FF",rightBorderColor:"#3366FF",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64"
                      ,subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}},
      mediaModeFields: [
        {type:"color",label:"mediaMode.fillColorX",key:"leftFillColor",labelParams:[1],home:true},
        {type:"color",label:"mediaMode.fillColorX",key:"rightFillColor",labelParams:[2],away:true},
        {type:"text",label:"mediaMode.titleX",key:"titleLeft",labelParams:[1],home:true},
        {type:"text",label:"mediaMode.titleX",key:"titleRight",labelParams:[2],away:true},
        {type:"number",label:"mediaMode.titleFontSize",key:"titleFontSize",force:true},
        {type:"radio",label:"mediaMode.titleFontWeight",key:"titleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"text",label:"mediaMode.subtitle",key:"subtitle"},
        {type:"number",label:"mediaMode.subtitleFontSize",key:"subtitleFontSize"},
        {type:"radio",label:"mediaMode.subtitleFontWeight",key:"subtitleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"text",label:"mediaMode.subtitleX",key:"subtitle2",labelParams:[2]},
        {type:"number",label:"mediaMode.subtitleFontSizeX",key:"subtitle2FontSize",labelParams:[2]},
        {type:"radio",label:"mediaMode.subtitleFontWeightX",key:"subtitle2FontWeight",labelParams:[2]
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"color",label:"mediaMode.textColor",key:"textColor"},
        {type:"color",label:"mediaMode.bgColor",key:"bgColor"},
        {type:"number",label:"mediaMode.avatarLineLength",key:"lineLength",force:true},
        {type:"number",label:"mediaMode.avatarLineWeight",key:"lineWeight",force:true},
        {type:"number",label:"mediaMode.avatarSize",key:"avatarSize",force:true},
        {type:"switch",label:"mediaMode.socialMedia",key:"socialMedia"},
        {type:"number",label:"mediaMode.socialMediaFontSize",key:"socialMediaFontSize",force:true},
        {type:"radio",label:"mediaMode.socialMediaFontWeight",key:"socialMediaFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"image",label:"mediaMode.sponsor",key:"sponsor",force:true,aspectRatio:16/9,imageRestriction:'none',resizable:true,stencilSize:{width:300,height:300}}
      ],
      chartSize: null,
      filteredPassesMatrix: [],
      loadingExport: false,
      showCircles: true,
      showLines: true,
      isEmbed: false
    };
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
    this.loadGame();
    //Get all passes (home team and away team) just once
    GameService.getGamePassMapData(
      this.gameId,
      this.minutes[0],
      this.minutes[1],
      this.periodId
    ).then(async (playersPasses) => {
      this.gameData = playersPasses.data;
      //Get Home and Away team name
      this.homeTeamName = this.gameData[0].teamName;
      this.awayTeamName = this.gameData[1].teamName;

      //Get Home or Away team game data
      this.gameDataByTeam = this.teamModel
        ? this.gameData[0]
        : this.gameData[1];
      //Get Avg Position of 11 players of selected team with more minutes played
      this.playersAvgPosition = this.gameDataByTeam.playersAvgPosition.slice(
        0,
        11
      );
      //Get 11 Player Ids of selected team with more minutes played
      this.playerIdsWithMoreMinutesPlayed = this.playersAvgPosition.map(
        function(player) {
          return player.playerId;
        }
      );

      this.filteredPasses = this.gameDataByTeam.playersPasses.filter(player =>
        this.playerIdsWithMoreMinutesPlayed.includes(player.playerFromId) && player.playerFromId != player.playerToId
      );

      this.filteredPassesMatrix = this.gameDataByTeam.playersPasses.filter(player =>
        player.playerFromId != player.playerToId
      );

      //Create map with playerId as Key and PlayerName as value
      this.createMapPlayerIdPlayerName(
        _.concat(
          this.gameData[0].playersAvgPosition,
          this.gameData[1].playersAvgPosition
        )
      );
      //Get Players Data
      this.passingStatsItems = this.gameDataByTeam.playersData;
      this.$emit("passingStats", this.passingStatsItems);
      this.updateTeamInfo();
      this.calculatePlayersConnections();

      this.setMinutes();
      UserService.putLastSearchRendered({id:this.lastSearchId});

      // Load individual pass events for the selected team
      await this.loadPassEvents();
    });
  },
  mounted: function() {
    this.mediaMode = (this.currentUser.mediaMode || this.currentUser.clientType === 'Media');
    setTimeout(() => {
      if (this.$refs.chart) {
        this.size = this.$refs.chart.clientWidth * 0.95;
      }
    }, 100);
  },
  methods: {
    formatDecimal(val, decimals) {
      if (val == null || val === '' || isNaN(Number(val))) return '';
      const n = Number(val);
      return n.toFixed(decimals);
    },
    async loadPassEvents() {
      if (!this.gameData) return;
      this.passEventsLoaded = false;
      try {
        const teamData = this.teamModel ? this.gameData[0] : this.gameData[1];
        const avgPositionIds = teamData.playersAvgPosition.map(p => p.playerId);
        const dataIds = teamData.playersData ? teamData.playersData.map(p => p.playerId) : [];
        const playerIds = [...new Set([...avgPositionIds, ...dataIds])];
        const response = await GameService.getPassMapEvents(
          [this.gameId], playerIds, 0, 150
        );
        const raw = response.data;
        this.passEvents = Array.isArray(raw) ? raw : (raw && raw.content) || (raw && raw.data) || [];
        this.passEventsKey += 1;
      } catch (err) {
        console.error('Error loading pass events:', err);
      } finally {
        this.passEventsLoaded = true;
      }
    },
    onWindowResize(e, force = false) {
      setTimeout(() => {
        if (this._isDestroyed || this._isBeingDestroyed || !this.$refs.chart) {
          return;
        }

        force = force || (e && e.type === "resize");
        if (this.size !== this.$refs.chart.clientWidth * 0.95) {
          this.size = this.$refs.chart.clientWidth * 0.95;
        }
        this.passEventsKey++;

        if (this.$refs.teamAvatarMediaRef) {
          this.$refs.teamAvatarMediaRef.printD3(force);
        }
        if (this.$refs.driblabSocialMediaRef) {
          this.$refs.driblabSocialMediaRef.printD3(force);
        }        
        if (this.$t) {
          this.$forceUpdate();
        }
      }, 100);
    },
    filterPasses(filterType) {
      let minFrom = this.minutes[0];
      let minTo = this.minutes[1];

      if (filterType == "minutes") {
        Promise.all([
          GameService.getAvgPlayersPositionsPassMap(
            this.gameId,
            minFrom,
            minTo,
            this.periodId,
            this.teamModel
          ),
          GameService.getPlayerDataPassMap(
            this.gameId,
            minFrom,
            minTo,
            this.periodId
          )
        ]).then(([positionsResponse, playerDataResponse]) => {
          let teamPosition = this.teamModel ? 0 : 1;
          //Get Avg Position of 11 players of selected team with more minutes played
          this.playersAvgPosition = positionsResponse.data.slice(0, 11);
          //Update gameData array with new AvgPlayersPositions
          this.gameData[
            teamPosition
          ].playersAvgPosition = this.playersAvgPosition;
          //Get 11 Player Ids of selected team with more minutes played
          this.playerIdsWithMoreMinutesPlayed = this.playersAvgPosition.map(
            function(player) {
              return player.playerId;
            }
          );

          this.filteredPasses = this.filteredPasses.filter(player =>
            this.playerIdsWithMoreMinutesPlayed.includes(player.playerFromId) && player.playerFromId != player.playerToId
          );
          this.gameDataByTeam = this.teamModel
            ? this.gameData[0]
            : this.gameData[1];

          //Get Avg Position of 11 players of selected team with more minutes played
          this.playersAvgPosition = this.gameDataByTeam.playersAvgPosition.slice(
            0,
            11
          );
          //Get 11 Player Ids of selected team with more minutes played
          this.playerIdsWithMoreMinutesPlayed = this.playersAvgPosition.map(
            function(player) {
              return player.playerId;
            }
          );
          //Get passes from team selected (Home or Away) and just 11 players with more minutes played
          this.filteredPasses = this.gameDataByTeam.playersPasses.filter(
            player =>
              this.playerIdsWithMoreMinutesPlayed.includes(player.playerFromId)  && player.playerFromId != player.playerToId
          );
          this.filteredPassesMatrix = this.gameDataByTeam.playersPasses.filter(
            player =>
              player.playerFromId != player.playerToId
          );

          //Get Players Data for the selected minute range; merge with full roster so rows stay consistent (zeros when no stats in window)
          this.passingStatsItems = this.normalizePassingStatsWithFullRoster(
            playerDataResponse.data || []
          );
          this.$emit("passingStats", this.passingStatsItems);
          if (this.passesType !== "all") {
            this.filteredPasses = this.filteredPasses.filter(
              pass =>
                pass[this.passesType] === true &&
                pass.timeEvent.split(":")[0] >= minFrom &&
                pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
            );
            this.filteredPassesMatrix = this.filteredPassesMatrix.filter(
              pass =>
                pass[this.passesType] === true &&
                pass.timeEvent.split(":")[0] >= minFrom &&
                pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
            );
          } else {
            this.filteredPasses = this.filteredPasses.filter(
              pass =>
                pass.timeEvent.split(":")[0] >= minFrom &&
                pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
            );
            this.filteredPassesMatrix = this.filteredPassesMatrix.filter(
              pass =>
                pass.timeEvent.split(":")[0] >= minFrom &&
                pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
            );
          }
          this.calculatePlayersConnections();
          //Update key to render map again
          this.passMapKey += 1;
          this.passMatrixKey += 1;
        });
      } else {
        //Filter by Home(teamModel = true) or Away(teamModel = false) team
        this.gameDataByTeam = this.teamModel
          ? this.gameData[0]
          : this.gameData[1];

        //Get Avg Position of 11 players of selected team with more minutes played
        this.playersAvgPosition = this.gameDataByTeam.playersAvgPosition.slice(
          0,
          11
        );
        //Get 11 Player Ids of selected team with more minutes played
        this.playerIdsWithMoreMinutesPlayed = this.playersAvgPosition.map(
          function(player) {
            return player.playerId;
          }
        );
        //Get passes from team selected (Home or Away) and just 11 players with more minutes played
        this.filteredPasses = this.gameDataByTeam.playersPasses.filter(player =>
          this.playerIdsWithMoreMinutesPlayed.includes(player.playerFromId) && player.playerFromId != player.playerToId
        );
        this.filteredPassesMatrix = this.gameDataByTeam.playersPasses.filter(
          player =>
            player.playerFromId != player.playerToId
        );

        //Get Players Data
        this.passingStatsItems = this.gameDataByTeam.playersData;
        if (this.passesType !== "all") {
          this.filteredPasses = this.filteredPasses.filter(
            pass =>
              pass[this.passesType] === true &&
              pass.timeEvent.split(":")[0] >= minFrom &&
              pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
          );
          this.filteredPassesMatrix = this.filteredPassesMatrix.filter(
            pass =>
              pass[this.passesType] === true &&
              pass.timeEvent.split(":")[0] >= minFrom &&
              pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
          );
        } else {
          this.filteredPasses = this.filteredPasses.filter(
            pass =>
              pass.timeEvent.split(":")[0] >= minFrom &&
              pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
          );
          this.filteredPassesMatrix = this.filteredPassesMatrix.filter(
            pass =>
              pass.timeEvent.split(":")[0] >= minFrom &&
              pass.timeEvent.split(":")[0] < minTo && pass.playerFromId != pass.playerToId
          );
        }
        this.calculatePlayersConnections();
        if (filterType == "team") {
          this.updateTeamInfo();
          this.selectedPlayer = '';
          this.selectedReceiver = '';
          this.passOutcome = 'all';
          this.passDirection = 'all';
          this.passHalf = 'all';
          this.loadPassEvents();
        }
        //Update key to render map again
        this.passMapKey += 1;
        this.passMatrixKey += 1;

        this.$forceUpdate();
      }
    },
    getReceiverName(e) {
      return (e.rN != null ? e.rN : null) || (e.receiverName != null ? e.receiverName : null) || (e.receiver_name != null ? e.receiver_name : null);
    },
    getPassDirection(event) {
      const dx = event.x_end - event.x;
      const dy = event.y_end - event.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 32) return 'longBall';
      if (Math.abs(dx) < Math.abs(dy)) return 'sideways';
      return dx > 0 ? 'forward' : 'backward';
    },
    updateTeamInfo() {
      this.teamId = this.gameDataByTeam.teamId;
      this.teamName = this.gameDataByTeam.teamName;
      this.teamFormation = this.gameDataByTeam.formation;
      this.teamColor = this.gameDataByTeam.teamColor;
      this.playersAvgPosition = this.gameDataByTeam.playersAvgPosition.slice(
        0,
        11
      );
    },
    calculatePlayersConnections() {
      this.passesByPlayersName = this.filteredPasses.reduce((acc, p) => {
        if(p.playerFromId != p.playerToId){
          const pFrom = p.playerFromName;
          const pTo = p.playerToName;
          if (!(pFrom in acc)) {
            acc[pFrom] = {};
          }
          if (!(pTo in acc[pFrom])) {
            acc[pFrom][pTo] = 1;
          } else {
            acc[pFrom][pTo] += 1;
          }
        }
        return acc;
      }, {});

      let passesByPlayersNameMatrixTemp = this.filteredPassesMatrix.reduce((acc, p) => {
        if(p.playerFromId != p.playerToId){
          const pFrom = p.playerFromName;
          const pTo = p.playerToName;
          if (!(pFrom in acc)) {
            acc[pFrom] = {};
          }
          if (!(pTo in acc[pFrom])) {
            acc[pFrom][pTo] = 1;
          } else {
            acc[pFrom][pTo] += 1;
          }
        }
        return acc;
      }, {});

      this.connectionsItems = _.chain(passesByPlayersNameMatrixTemp)
        .map((passes, playerFrom) => {
          return _.map(passes, (numberOfPasses, playerTo) => {
            return { playerFrom, playerTo, numberOfPasses };
          });
        })
        .flatten()
        .orderBy("numberOfPasses", "desc")
        .value()
        .slice(0, 30);

      this.$emit("connections", this.connectionsItems);
    },
    //Create map with playerId as Key and PlayerName as value
    createMapPlayerIdPlayerName(avgPositions) {
      this.playerNamePlayerIdMap = avgPositions.reduce((acc, player) => {
        acc[player.playerName] = player.playerId;
        return acc;
      }, {});
    },
    async saveAsPNG(type) {
      type = type || this.currentChartType;
      const fileName = `Driblab_${type}_${this.game.homeTeamName.replace(/ /g,'_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(/ /g,'_')}.png`;
      
      if (this.mediaMode && (this.displayModel === 'map')) {
        const exportEl = document.getElementById('chart');
        if (exportEl) {
          try {
            if (document.fonts && document.fonts.ready) {
              await document.fonts.ready;
            }

            const imageElements = exportEl.querySelectorAll('img, image');
            const base64ImagesMap = new Map();

            for (const imgEl of imageElements) {
              const url = imgEl.getAttribute('xlink:href') || imgEl.getAttribute('href') || imgEl.getAttribute('src');
              if (url && url.startsWith('http') && !base64ImagesMap.has(url)) {
                try {
                  const base64 = await imageUrlToBase64(url);
                  base64ImagesMap.set(url, base64);
                } 
                catch (error) {
                  console.error(`Error converting to Base64: ${url}`, error);
                }
              }
            }

            const canvas = await html2canvas(exportEl, {
              useCORS: true,
              scale: 2,
              backgroundColor: this.mediaModeData.bgColor || '#ffffff',
              logging: false,
              onclone: (clonedDoc) => {
                const clonedImages = clonedDoc.querySelectorAll('#chart img, #chart image');
                clonedImages.forEach((clonedImg) => {
                  const originalUrl = clonedImg.getAttribute('xlink:href') || clonedImg.getAttribute('href') || clonedImg.getAttribute('src');
                  if (originalUrl && base64ImagesMap.has(originalUrl)) {
                    const base64Data = base64ImagesMap.get(originalUrl);
                    if (clonedImg.tagName.toLowerCase() === 'image') {
                      clonedImg.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', base64Data);
                      clonedImg.setAttribute('href', base64Data);
                    } else {
                      clonedImg.setAttribute('src', base64Data);
                    }
                  }
                });
              }
            });

            const link = document.createElement('a');
            link.download = fileName;
            link.href = canvas.toDataURL('image/png');
            link.click();
          } catch (error) {
            console.error("Error generating the image:", error);
          }
        }
        return;
      }

      if (this.mode === 'pass-map' && this.displayModel === 'map') {
        const containerId = `pitchPassMap-${this.chartId}`;
        const exportEl = document.getElementById(containerId);
        
        if (exportEl) {
          try {
            if (document.fonts && document.fonts.ready) {
              await document.fonts.ready;
            }

            const imageElements = exportEl.querySelectorAll('img, image');
            const base64ImagesMap = new Map();

            for (const imgEl of imageElements) {
              const url = imgEl.getAttribute('xlink:href') || imgEl.getAttribute('href') || imgEl.getAttribute('src');
              if (url && url.startsWith('http') && !base64ImagesMap.has(url)) {
                try {
                  const base64 = await imageUrlToBase64(url);
                  base64ImagesMap.set(url, base64);
                } 
                catch (e) {
                  console.error(`Error converting to Base64: ${url}`, e);
                }
              }
            }

            const canvas = await html2canvas(exportEl, {
              useCORS: true,
              scale: 2,
              backgroundColor: this.$vuetify.theme.dark ? '#1e1e1e' : '#ffffff',
              logging: false,
              onclone: (clonedDoc) => {
                const clonedImages = clonedDoc.querySelectorAll(`#${containerId} img, #${containerId} image`);
                
                clonedImages.forEach((clonedImg) => {
                  const originalUrl = clonedImg.getAttribute('xlink:href') || clonedImg.getAttribute('href') || clonedImg.getAttribute('src');
                  if (originalUrl && base64ImagesMap.has(originalUrl)) {
                    const base64Data = base64ImagesMap.get(originalUrl);
                    if (clonedImg.tagName.toLowerCase() === 'image') {
                      clonedImg.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', base64Data);
                      clonedImg.setAttribute('href', base64Data);
                    } 
                    else {
                      clonedImg.setAttribute('src', base64Data);
                    }
                  }
                });
              }
            });
            const link = document.createElement('a');
            link.download = fileName;
            link.href = canvas.toDataURL('image/png');
            link.click();
          } 
          catch (error) {
            console.error("Error exporting to PNG:", error);
            const svg = exportEl.querySelector("svg");
            if (svg) {
              downloadPng(svg, fileName);
            }
          }
        }
        return;
      }

      if (this.mode === 'connections' && this.displayModel === 'map') {
        const legacyContainerId = `${this.gameId}-${this.teamId}-${this.chartId}`;
        const exportEl = document.getElementById(legacyContainerId);
        const svg = exportEl ? exportEl.querySelector("svg") : null;
        
        if (svg) {
          downloadPng(svg, fileName);
        } else {
          let fallbackSvg = d3.select(`#pitch${type}${this.gameId}-${this.teamId}-${this.chartId}`).select("svg").node();
          if (fallbackSvg) downloadPng(fallbackSvg, fileName);
        }
        return;
      }

      // Matrix: use html2canvas to preserve fonts and logo
      if (this.mode === 'connections' && this.displayModel === 'matrix') {
        const exportEl = document.getElementById('passMatrixExport');
        if (exportEl) {
          const captureMatrix = async () => {
            if (document.fonts && document.fonts.ready) await document.fonts.ready;
            const whiteLogoDataUri = await getDriblabWhiteLogoDataUri();
            const canvas = await html2canvas(exportEl, {
              useCORS: true,
              scale: 2,
              backgroundColor: this.$vuetify.theme.dark ? '#1e1e1e' : '#ffffff',
              logging: false,
              onclone: (clonedDoc) => {
                const logoUri = this.$vuetify.theme.dark ? whiteLogoDataUri : BLACK_LOGO_DATA_URI;
                const logo = clonedDoc.querySelector('#driblab-logo');
                if (logo) {
                  logo.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', logoUri);
                  logo.setAttribute('href', logoUri);
                }
              },
            });
            const link = document.createElement('a');
            link.download = fileName;
            link.href = canvas.toDataURL('image/png');
            link.click();
          };
          captureMatrix();
        }
        return;
      }

      // Table views: use html2canvas
      if (this.displayModel === 'table') {
        const exportId = this.mode === 'pass-map' ? 'passMapTableExport' : 'passMapConnectionsTableExport';
        const exportEl = document.getElementById(exportId);
        if (exportEl) {
          const wrappers = exportEl.querySelectorAll('.v-data-table__wrapper');
          const originalOverflows = [];
          wrappers.forEach((w) => {
            originalOverflows.push(w.style.overflow);
            w.style.overflow = 'visible';
          });
          try {
            const canvas = await html2canvas(exportEl, {
              useCORS: true,
              scale: 2,
              backgroundColor: '#ffffff',
              logging: false,
            });
            const link = document.createElement('a');
            link.download = fileName;
            link.href = canvas.toDataURL('image/png');
            link.click();
          } finally {
            wrappers.forEach((w, i) => {
              w.style.overflow = originalOverflows[i] || '';
            });
          }
        }
        return;
      }

      let svg = d3.select(`#pitch${type}${this.gameId}-${this.teamId}-${this.chartId}`).select("svg").node();
      downloadPng(svg, fileName);
    },
    async saveAsSVG() {
      let type = this.currentChartType;
      const fileName = `Driblab_${type}_${this.game.homeTeamName.replace(/ /g,'_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(/ /g,'_')}.svg`;
      
      if (this.mediaMode && (this.displayModel === 'map' || this.displayModel === 'matrix' || this.mode === 'connections')) {
        try {
          const mediaSvg = await this.prepareSvgForMedia();
          if (mediaSvg) {
            exportSvg(mediaSvg, fileName);
          }
        } 
        catch (error) {
          console.error("Error generating the PNG:", error);
        }
        return;
      }

      // Table views: capture with html2canvas, embed in SVG
      if (this.displayModel === 'table') {
        const exportId = this.mode === 'pass-map' ? 'passMapTableExport' : 'passMapConnectionsTableExport';
        const exportEl = document.getElementById(exportId);
        if (exportEl) {
          const wrappers = exportEl.querySelectorAll('.v-data-table__wrapper');
          const originalOverflows = [];
          wrappers.forEach((w) => {
            originalOverflows.push(w.style.overflow);
            w.style.overflow = 'visible';
          });
          html2canvas(exportEl, { useCORS: true, scale: 2, backgroundColor: '#ffffff', logging: false }).then((canvas) => {
            try {
              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
              svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
              svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
              svg.setAttribute('width', canvas.width);
              svg.setAttribute('height', canvas.height);
              const img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
              img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', canvas.toDataURL('image/png'));
              img.setAttribute('x', 0);
              img.setAttribute('y', 0);
              img.setAttribute('width', canvas.width);
              img.setAttribute('height', canvas.height);
              svg.appendChild(img);
              exportSvg(svg, fileName);
            } finally {
              wrappers.forEach((w, i) => {
                w.style.overflow = originalOverflows[i] || '';
              });
            }
          });
        }
        return;
      }

      // Matrix: capture with html2canvas to preserve fonts and logo
      if (this.mode === 'connections' && this.displayModel === 'matrix') {
        const exportEl = document.getElementById('passMatrixExport');
        if (exportEl) {
          const captureMatrix = async () => {
            if (document.fonts && document.fonts.ready) await document.fonts.ready;
            const whiteLogoDataUri = await getDriblabWhiteLogoDataUri();
            const canvas = await html2canvas(exportEl, {
              useCORS: true,
              scale: 2,
              backgroundColor: this.$vuetify.theme.dark ? '#1e1e1e' : '#ffffff',
              logging: false,
              onclone: (clonedDoc) => {
                const logoUri = this.$vuetify.theme.dark ? whiteLogoDataUri : BLACK_LOGO_DATA_URI;
                const logo = clonedDoc.querySelector('#driblab-logo');
                if (logo) {
                  logo.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', logoUri);
                  logo.setAttribute('href', logoUri);
                }
              },
            });
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
            svg.setAttribute('width', canvas.width);
            svg.setAttribute('height', canvas.height);
            const img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
            img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', canvas.toDataURL('image/png'));
            img.setAttribute('x', 0);
            img.setAttribute('y', 0);
            img.setAttribute('width', canvas.width);
            img.setAttribute('height', canvas.height);
            svg.appendChild(img);
            exportSvg(svg, fileName);
          };
          captureMatrix();
        }
        return;
      }

      if (this.mode === 'pass-map' && this.displayModel === 'map') {
        const containerId = `pitchPassMap-${this.chartId}`;
        const container = document.getElementById(containerId);        
        const svg = container ? container.querySelector("svg") : null;      
        if (svg) {
          exportSvg(svg, fileName);
        }
        return;
      }

      if (this.mode === 'connections' && this.displayModel === 'map') {
        const legacyContainerId = `${this.gameId}-${this.teamId}-${this.chartId}`;
        const container = document.getElementById(legacyContainerId);
        const svg = container ? container.querySelector("svg") : null;
        
        if (svg) {
          exportSvg(svg, fileName);
        } else {
          let fallbackSvg = d3.select(`#pitch${type}${this.gameId}-${this.teamId}-${this.chartId}`).select("svg").node();
          if (fallbackSvg) exportSvg(fallbackSvg, fileName);
        }
        return;
      }

      let fallbackSvg = d3.select(`#pitch${type}${this.gameId}-${this.teamId}-${this.chartId}`).select("svg").node();
      if (fallbackSvg) {
        exportSvg(fallbackSvg, fileName);
      }
    },
    async getElementToExport(){
      if (this.mediaMode) {
        return await this.prepareSvgForMedia(this.currentChartType);
      }

      if (this.mode === 'pass-map' && this.displayModel === 'map') {
        return null;
      }
      
      let type = this.currentChartType;
      let svg = d3.select(`#pitch${type}${this.gameId}-${this.teamId}-${this.chartId}`).select("svg").node();
      return svg;
    },
    setMinutes(){
      let allPasses = _.reduce(this.gameData,(result,g) => {
        result = [...result,...g.playersPasses];
        return result;
      },[])
      let allMinutes = _.reduce(allPasses,(result,p) => {
        let minute = Number(_.split(p.timeEvent,':')[0]);
        result.push(minute);
        return result;
      },[])
      this.rangeMinutes[0] = 0;
      this.rangeMinutes[1] = _.max(allMinutes);
      this.minutes = this.rangeMinutes;
    },
    getBackgroundColor(item,property,inverted = false){
      let allData = _.map(this.passingStatsItems,(t) => t[property]);
      let max = _.max(allData);
      if (max == null || max === 0) max = 1;
      let data = item[property];
      let percentageOfColor = data / max;
      if(inverted){
        percentageOfColor = 1 - percentageOfColor;
      }
      return `rgba(51, 102, 255,${percentageOfColor})`
    },
    getTextColorClass(item,property,inverted = false){
      let allData = _.map(this.passingStatsItems,(t) => t[property]);
      let max = _.max(allData);
      if (max == null || max === 0) max = 1;
      let percentageOfColor = item[property] / max;
              if(inverted){
          percentageOfColor = 1 - percentageOfColor;
        }
      if (percentageOfColor > 0.6) return 'white--text';
      return this.$vuetify.theme.dark ? 'grey--text text--lighten-2' : 'grey--text text--darken-4';
    },
    /**
     * Minute-range API can omit players with no events or return a sparse list.
     * Align with full-game roster (gameDataByTeam.playersData) so every player row stays visible with 0s when needed.
     */
    normalizePassingStatsWithFullRoster(apiRows) {
      const teamId = this.gameDataByTeam.teamId;
      const filteredApi = (apiRows || []).filter(p => p.teamId === teamId);
      const byId = {};
      filteredApi.forEach(row => {
        byId[row.playerId] = row;
      });
      // playersData is already scoped to the selected side (home/away)
      const roster = this.gameDataByTeam.playersData || [];
      if (!roster.length) {
        return filteredApi;
      }
      const seen = new Set();
      const merged = roster.map(p => {
        seen.add(p.playerId);
        const row = byId[p.playerId];
        if (row) return { ...row };
        return {
          playerId: p.playerId,
          playerName: p.playerName,
          teamId,
          totalPasses: 0,
          pctPasses: 0,
          xA: 0,
          xGChain: 0,
          xT: 0
        };
      });
      filteredApi.forEach(row => {
        if (!seen.has(row.playerId)) {
          merged.push(row);
          seen.add(row.playerId);
        }
      });
      return merged;
    },
    /** Precompute heatmap colors for embedded table JSON (same scale as getBackgroundColor / getTextColorClass). */
    buildPassingStatsItemsForEmbed(items) {
      if (!items || !items.length) return [];
      const heatKeys = ['xGChain', 'xA', 'xT'];
      return items.map(item => {
        const row = { ...item };
        heatKeys.forEach(key => {
          const allData = items.map(t => t[key]);
          const max = _.max(allData);
          const safeMax = max > 0 ? max : 1;
          let pct = item[key] / safeMax;
          row[`_heatmap_${key}_bg`] = `rgba(51, 102, 255,${pct})`;
          row[`_heatmap_${key}_color`] = pct > 0.6
            ? '#FFFFFF'
            : (this.$vuetify.theme.dark ? '#EEEEEE' : '#212121');
        });
        return row;
      });
    },
    onMediaModeChange(event){
      this.mediaMode = event;
    },
    onMediaModeDataChange(event){
      this.mediaModeData = {...event};
      setTimeout(() => {this.onWindowResize(null,this.mediaModeData.force);},666)
    },
    async loadGame(){
      let gameResponse = await GameService.getGame(this.gameId);
      this.game = gameResponse.data;

      this.mediaModeData.subtitle = this.game.leagueName;
      this.mediaModeData.subtitle2 = this.$t("game.header.gamePassMap");
      this.mediaModeData.titleLeft = this.game.homeTeamName;
      this.mediaModeData.titleRight = this.game.awayTeamName;
      this.mediaModeData.leftFillColor = this.game.homeTeamColor ? this.game.homeTeamColor.replace("0x","#") : getNeutralColors('home')[0];
      this.mediaModeData.rightFillColor = this.game.awayTeamColor ? this.game.awayTeamColor.replace("0x","#") : getNeutralColors('away')[0];
      this.mediaModeData.leftBorderColor = this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor.replace("0x","#") : getNeutralColors('home')[1];
      this.mediaModeData.rightBorderColor = this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor.replace("0x","#") : getNeutralColors('away')[1];
      this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
      this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
      this.mediaModeFields = _.map(this.mediaModeFields,(f) => {
        if(f.home){
          f.labelParams = [this.game.homeTeamName];
        }
        if(f.away){
          f.labelParams = [this.game.awayTeamName];
        }
        return f;
      })
      this.$forceUpdate();
    },
    async prepareSvgForMedia() {
      let parent = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      let heightAccum = 0;
      let parentWidth = this.size;

      // Avatar (Header)
      let avatarSvg = null;
      const avatarRef = this.$refs.gameAvatarMediaRef;
      if (avatarRef) {
        const el = avatarRef.$el || avatarRef;
        avatarSvg = el.tagName && el.tagName.toLowerCase() === 'svg' ? el : el.querySelector("svg");
      }

      if (avatarSvg) {
        copyAndAppendElement(parent, avatarSvg, 0, 0);
      }
      heightAccum += (this.avatarSize ? this.avatarSize.height : 0);

      let pitchSvg = null;

      // Select Pass Map
      const pitchContainer = document.getElementById(`pitchPassMap-${this.chartId}`);
      pitchSvg = pitchContainer ? pitchContainer.querySelector("svg") : null;

      // Selecrt Connections
      if (!pitchSvg) {
        const legacyContainerId = `${this.gameId}-${this.teamId}-${this.chartId}`;
        const legacyContainer = document.getElementById(legacyContainerId);
        pitchSvg = legacyContainer ? legacyContainer.querySelector("svg") : null;
      }

      // Select by chart type if no of tghe above options works
      if (!pitchSvg && this.currentChartType) {
        pitchSvg = d3.select(`#pitch${this.currentChartType}${this.gameId}-${this.teamId}-${this.chartId}`).select("svg").node();
      }

      if (pitchSvg) {
        copyAndAppendElement(parent, pitchSvg, 0, heightAccum);
        const pitchHeight = parseFloat(pitchSvg.getAttribute("height")) || (this.size * 0.8);
        heightAccum += pitchHeight;
      } else if (this.chartSize && this.chartSize.height) {
        heightAccum += this.chartSize.height;
      }

      // Footer
      if (this.mediaModeData && this.mediaModeData.socialMedia) {
        let socialSvg = null;
        if (this.$refs.driblabSocialMediaRef) {
          const el = this.$refs.driblabSocialMediaRef.$el || this.$refs.driblabSocialMediaRef;
          socialSvg = el.tagName && el.tagName.toLowerCase() === 'svg' ? el : el.querySelector("svg");
        }

        if (socialSvg) {
          copyAndAppendElement(parent, socialSvg, 0, heightAccum);
        }
        heightAccum += 50;
      }

      // Background
      let bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bgRect.setAttribute('width', '100%');
      bgRect.setAttribute('height', '100%');
      bgRect.setAttribute('fill', this.mediaModeData.bgColor);

      parent.insertBefore(bgRect, parent.firstChild);
      parent.setAttribute("width", parentWidth);
      parent.setAttribute("height", heightAccum);
      parent.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      parent.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
      parent.style.background = this.mediaModeData.bgColor;

      await this.embedImagesInSvg(parent);

      return parent;
    },
    async embedImagesInSvg(svgElement) {
      const images = svgElement.querySelectorAll('image');
      
      const promises = Array.from(images).map(async (img) => {
        const href = img.getAttribute('href') || img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
        if (href && !href.startsWith('data:')) {
          try {
            const base64Data = await imageUrlToBase64(href);
            img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', base64Data);
            img.setAttribute('href', base64Data);
          } catch (err) {
            console.warn('Could not convert to Base 64: ', href, err);
          }
        }
      });
      await Promise.all(promises);
    },
    onChartSizeChange(event){
      this.chartSize = event;
    },
    onLoaded(event){
      this.$emit("loaded", event);
    },
    isWhite(color){
      return color === "#FFF" || color === "#fff" || color === "#FFFFFF" || color === "#ffffff" || color === "white";
    },
    async onGenerateIFrameClick() {
      this.isEmbed = true;
      this.loadingExport = true;
      let elToExport = await this.getElementToExport();
      let width, height;
      let chartType = 'game-pass-map-d3';

      if (this.mode === 'pass-map' && this.displayModel === 'map') {
        const containerId = `pitchPassMap-${this.chartId}`;
        const container = document.getElementById(containerId);
        
        if (container) {
          const rect = container.getBoundingClientRect();
          width = Math.round(rect.width) || this.size || 560;
        } else {
          width = this.size || 560;
        }
        height = this.mediaMode ? width * 0.9925 : width * 0.806;
      } 
      else if (this.mode === 'pass-map' && this.displayModel === 'table') {
        const exportEl = document.getElementById('passMapTableExport');
        if (!exportEl || !(this.passingStatsItems && this.passingStatsItems.length)) {
          this.loadingExport = false;
          return;
        }
        const rect = exportEl.getBoundingClientRect();
        width = Math.round(rect.width) || 800;
        height = Math.round(rect.height) || 600;
        chartType = 'game-pass-map-table';
      } else if (this.mode === 'connections' && this.displayModel === 'table') {
        const exportEl = document.getElementById('passMapConnectionsTableExport');
        if (!exportEl || !(this.connectionsItems && this.connectionsItems.length)) {
          this.loadingExport = false;
          return;
        }
        const rect = exportEl.getBoundingClientRect();
        width = Math.round(rect.width) || 800;
        height = Math.round(rect.height) || 600;
        chartType = 'game-pass-connections-table';
      } else if (this.mode === 'connections' && this.displayModel === 'matrix') {
        chartType = 'game-pass-matrix';
        if (elToExport) {
          width = Number(elToExport.getAttribute("width"));
          height = Number(elToExport.getAttribute("height")) || (this.mediaMode ? width * 0.9925 : width * 0.806);
        } else {
          width = this.size || 800;
          height = this.mediaMode ? width * 0.9925 : width * 0.806;
        }
      } else if (elToExport) {
        width = Number(elToExport.getAttribute("width"));
        height = this.mediaMode ? width * 0.9925 : width * 0.806;
      } else {
        this.loadingExport = false;
        return;
      }

      let key = v4();
      key = `${key}.json`;
      let chartObject = {
        awsKey: key,
        tool: 'game-pass-map',
        category: 'game',
        componentId: this.gameId,
        width: String(width),
        height: String(height)
      };
      let postResponse = await UserChartService.postChart(chartObject);
      let chartEntity = postResponse.data;

      this.$refs.chartExportLinksModalRef.open(chartEntity);
      this.loadingExport = false;

      let urlResponse = await UserChartService.getUploadURL(chartEntity.id, key);
      let uploadUrl = urlResponse.data;

      let jsonPayload = {
        size: Number(chartEntity.width),
        height: Number(chartEntity.height),
        chartType,
        category: 'game',
        game: this.game,
        language: this.currentUserLanguage,
        id: chartType === 'game-pass-matrix' ? `${this.gameId}-${this.teamId}-${this.chartId}` : `${this.gameId}-${this.chartId}`,
      };

      if (chartType === 'game-pass-map-d3') {
        jsonPayload.passes = this.filteredPassEventsForChart;
        jsonPayload.gameName = this.game ? `${this.game.homeTeamName} ${this.game.homeTeamScore} - ${this.game.awayTeamScore} ${this.game.awayTeamName}` : '';
        jsonPayload.filterLine = this.passMapFilterLine;
        jsonPayload.typeFilterLabel = this.passDirection !== 'all' ? this.$t(`game.passMap.${this.passDirection === 'forward' ? 'forwards' : this.passDirection === 'backward' ? 'backwards' : this.passDirection}`) : '';
        jsonPayload.eventFilterLabel = this.passOutcome !== 'all' ? (this.passOutcome === 'successful' ? (this.$t('eventChart.successful') || 'Successful') : (this.$t('eventChart.unsuccessful') || 'Unsuccessful')) : '';
        jsonPayload.touchesLabel = this.$t('eventChart.totalPasses');
        jsonPayload.team = this.teamModel
        jsonPayload.showCircles = this.showCircles;
        jsonPayload.showLines = this.showLines;        
        jsonPayload.legendTitle = this.legendTitle;
        jsonPayload.legendTeamName = this.legendTeamName;
        jsonPayload.legendSubtitle = this.legendSubtitle;
        jsonPayload.legendTeamCrest = this.legendTeamCrest;
        jsonPayload.legendMetrics = this.legendMetrics;
        jsonPayload.mediaMode = this.mediaMode;
        jsonPayload.mediaModeData = this.mediaModeData;
        jsonPayload.touchesLabel = this.$t('eventChart.totalPasses') || 'Total Passes';
        jsonPayload.player = { teamId: this.teamId };
        jsonPayload.orientation = this.orientationModel;
        jsonPayload.isEmbed = this.isEmbed;
        jsonPayload.embedFactor = this.orientationModel === 'vertical' ? 0.67 : 1;
      } else if (chartType === 'game-pass-map-table') {
        jsonPayload.headers = this.passingStatsHeader;
        jsonPayload.items = this.buildPassingStatsItemsForEmbed(this.passingStatsItems);
        jsonPayload.title = this.$t('table.playerPassMapData.title');
        jsonPayload.titleBarClass = this.$vuetify.theme.dark ? 'secondary' : 'primary';
        jsonPayload.isEmbed = this.isEmbed;
        jsonPayload.embedFactor = this.orientationModel === 'vertical' ? 0.67 : 1;
      } else if (chartType === 'game-pass-connections-table') {
        jsonPayload.headers = this.connectionsHeader;
        jsonPayload.items = this.connectionsItems;
        jsonPayload.title = this.$t('game.passMap.passingConnections');
        jsonPayload.titleBarClass = this.$vuetify.theme.dark ? 'secondary' : 'primary';
        jsonPayload.themeDark = this.$vuetify.theme.dark;
        jsonPayload.playerNamePlayerIdMap = this.playerNamePlayerIdMap || {};
        jsonPayload.mediaMode = this.mediaMode;
        jsonPayload.mediaModeData = this.mediaModeData;
        jsonPayload.isEmbed = this.isEmbed;
        jsonPayload.embedFactor = this.orientationModel === 'vertical' ? 0.67 : 1;
      } else if (chartType === 'game-pass-matrix') {
        jsonPayload.passes = this.filteredPassesMatrix;
        jsonPayload.homeTeamScore = this.game.homeTeamScore;
        jsonPayload.rivalTeamScore = this.game.awayTeamScore;
        jsonPayload.teamName = this.homeTeamName;
        jsonPayload.rivalTeamName = this.awayTeamName;
        jsonPayload.minFrom = this.minutes[0];
        jsonPayload.minTo = this.minutes[1];
        jsonPayload.themeDark = this.$vuetify.theme.dark;
        jsonPayload.language = this.currentUserLanguage;
        jsonPayload.margin = this.margin || { top: 75, right: 72, bottom: 132, left: 228 };
        jsonPayload.isEmbed = this.isEmbed;
        jsonPayload.embedFactor = this.orientationModel === 'vertical' ? 0.67 : 1;
      } else {
        jsonPayload.passes = this.filteredPassEventsForChart;
        jsonPayload.passesByPlayersName = this.passesByPlayersName;
        jsonPayload.playersAvgPosition = this.playersAvgPosition;
        jsonPayload.teamId = this.teamId;
        jsonPayload.teamFormation = this.teamFormation;
        jsonPayload.teamColor = this.teamModel ? this.mediaModeData.leftFillColor : this.mediaModeData.rightFillColor;
        jsonPayload.strokeColor = this.strokeColor;
        jsonPayload.teamName = this.teamModel ? this.mediaModeData.titleLeft : this.mediaModeData.titleRight;
        jsonPayload.minFrom = this.minutes[0];
        jsonPayload.minTo = this.minutes[1];
        jsonPayload.mediaModeData = this.mediaModeData;
        jsonPayload.mediaMode = this.mediaMode;
        jsonPayload.playerAvatarKey = 0;
        jsonPayload.key = this.passMapKey;
        jsonPayload.socialMediaKey = 2;
        jsonPayload.orientation = this.orientationModel;
        jsonPayload.isEmbed = this.isEmbed;
        jsonPayload.embedFactor = this.orientationModel === 'vertical' ? 0.67 : 1;
      }
      
      var blob = new Blob([JSON.stringify(jsonPayload)], { type: "application/json" });
      await DocumentService.upload(uploadUrl, blob);
      this.postSearchChart(chartEntity);
      this.isEmbed = false;
    },
    async postSearchChart(chart){
      const authorized = true;
      let metaInfo = null;
      let category = "game";
      let urlId = this.gameId;

      metaInfo = JSON.stringify({
        link: `${API_URL}${chart.id}/key/${chart.awsKey}/compId/${chart.componentId}/catId/${chart.categoryId}/tId/${chart.toolId}`
      });
      const tool = 'embed';
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = navigator.userAgent;
      let searchResponse = await UserService.postUserSearch(category, tool, urlId, authorized, this.$route.path, timeZone, browser, metaInfo);
      UserService.putLastSearchRendered({category,tool,id:searchResponse.data})
    },
    userHasRole(role) {
      return userHasRole(this.currentUser, role);
    },
  },
  destroyed() {
    d3.selectAll(".tooltip").remove();
    window.removeEventListener("resize", this.onWindowResize);
  }
};
</script>
<style lang="scss">
/* Matrix PNG/SVG: html2canvas needs visible overflow; label extends left of plot */
.pass-matrix-export-wrap {
  overflow: visible;
  box-sizing: border-box;
}

/* Horizontal inset for table + exports on narrow viewports */
.pass-map-table-export-wrap {
  box-sizing: border-box;
  max-width: 100%;
  @media (max-width: 959px) {
    padding-left: 20px;
    padding-right: 20px;
  }
  @media (max-width: 599px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

#passMapTable,
#passMapConnectionsTable {
  td, th {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }
}

/* Mobile stacked rows: restore horizontal padding inside cells (desktop keeps tight grid) */
@media (max-width: 959px) {
  #passMapTable,
  #passMapConnectionsTable {
    td,
    th {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
  }
}
@media (max-width: 599px) {
  #passMapTable,
  #passMapConnectionsTable {
    td,
    th {
      padding-left: 20px !important;
      padding-right: 20px !important;
    }
  }
}
</style>
<style>
body > div.tooltip {
  font-family: "NeueMontreal";
  pointer-events: none;
  position: absolute;
  background: none repeat scroll 0 0 #ffffff;
  padding: 5px 10px 5px 10px;
  border-radius: 4px;
  text-align: center;
  display: none;
  line-height: 1.5;
  color: #081f2c;
  box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.15);
}

body > div.tooltip:after {
  box-sizing: border-box;
  display: block;
  width: 100%;
  line-height: 2.8;
  color: #ffffff;
  content: "\25BC";
  text-align: center;
}

body > div.nwTooltip {
  font-family: "NeueMontreal";
  pointer-events: none;
  position: absolute;
  background: none repeat scroll 0 0 #ffffff;
  padding: 8px 12px;
  border-radius: 4px;
  display: none;
  line-height: 1.4;
  color: #081f2c;
  box-shadow: 0px 3px 9px rgba(0, 0, 0, 0.15);
}
</style>



// WEBPACK FOOTER //
// src/views/game/GamePassMap.vue